import classNames from 'classnames';
import { connect } from 'formik';
import React, { Component, PropsWithChildren, SyntheticEvent } from 'react';
import { getDescribedBy } from '../Input/inputUtils';
import { createId } from '../utils/IdGenerator';
import { resolveProps } from '../utils/formikUtils';
import './Toggle.scss';
import { IToggleProps, InnerToggleProps } from './types';

/**
 * Switches should be used sparingly as they have an inherent state-action ambiguity;
 * it is unclear whether the label is the current state or the action. The action of a switch
 * should submit immediately and not require a button to submit. In most cases checkboxes can be used instead.
 */
export class Toggle extends Component<PropsWithChildren<InnerToggleProps>, {}> {
  private id = this.props.id || createId('toggle');
  public handleClick = (event: SyntheticEvent<HTMLElement>) => {
    event.preventDefault();
    const { formik } = this.props;
    const toggled =
      this.props.toggled !== undefined ? this.props.toggled : formik && formik.values[this.props.name || ''];
    if (this.props.onChange) {
      this.props.onChange(this.props.name, !toggled);
    } else if (formik && formik.setFieldValue) {
      formik.setFieldValue(this.props.name, !toggled);
    }
  };
  public handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.keyCode === 32) {
      this.handleClick(event);
    }
  };
  public render() {
    const { id } = this;
    const { name = '', helpText, label } = this.props;
    const { toggled } = resolveProps(this.props, this.props.formik);

    const describedBy = getDescribedBy({
      hasErrors: false,
      hasHelpText: !!helpText,
      id,
    });
    const toggleClasses = classNames({
      toggle: true,
      'toggle-off': !toggled,
      'toggle-on': toggled,
    });
    const colorClass = 'btn-' + (this.props.color || 'success');
    const a11y = {
      'aria-describedby': describedBy,
      'aria-pressed': toggled,
      role: 'button',
      tabIndex: 0,
    };
    return (
      <div qa-id={this.props['qa-id']}>
        <div
          {...a11y}
          className={`${colorClass} ${toggleClasses}`}
          onClick={this.handleClick}
          onKeyDown={this.handleKeyDown}
          data-testid={this.props['qa-id']}
        >
          <input
            id={id}
            name={name}
            type="checkbox"
            checked={toggled}
            onChange={() => {
              // noop
            }}
          />
          <button aria-expanded={toggled} tabIndex={-1} />
        </div>{' '}
        {label && <label htmlFor={id}>{toggled ? label.on : label.off}</label>}
        {helpText && (
          <p id={`${id}HelpText`} className="hlc-form-element-help">
            {helpText}
          </p>
        )}
      </div>
    );
  }
}

export default connect<IToggleProps>(Toggle);
